import { PlatformRole } from '@repo/shared'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@repo/ui'
import {
  Outlet,
  createFileRoute,
  useRouter,
  useRouterState,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import useUser from '../lib/api/auth/me'

import { Header } from '@/components/layout/header/header'
import { AdminSidebar } from '@/features/admin/layout/admin-sidebar'
import { useUIStore } from '@/store/ui-store'

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
})

function AdminLayout() {
  const { user, isPending } = useUser()
  const router = useRouter()
  const isCollapsed = useUIStore((s) => s.sidebarCollapsed)
  const setIsCollapsed = useUIStore((s) => s.setSidebarCollapsed)
  const mobileNavOpen = useUIStore((s) => s.mobileNavOpen)
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen)
  const { t } = useTranslation('common')
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  useEffect(() => {
    if (isPending) return
    if (!user) {
      router.navigate({ to: '/login' })
      return
    }
    if (user.platformRole !== PlatformRole.SuperAdmin) {
      router.navigate({ to: '/dashboard' })
    }
  }, [user, isPending, router])

  useEffect(() => {
    setMobileNavOpen(false)
  }, [pathname, setMobileNavOpen])

  if (isPending) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
          <p className="text-muted-foreground mt-4">{t('loading')}</p>
        </div>
      </div>
    )
  }

  if (!user || user.platformRole !== PlatformRole.SuperAdmin) {
    return null
  }

  return (
    <div className="flex h-dvh w-full overflow-x-hidden">
      <div className="hidden md:block">
        <AdminSidebar
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
        />
      </div>
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          showCloseButton={false}
          className="w-full max-w-none p-0 sm:max-w-none md:hidden"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{t('layout.openNav')}</SheetTitle>
            <SheetDescription>{t('layout.openNav')}</SheetDescription>
          </SheetHeader>
          <AdminSidebar
            isCollapsed={false}
            setIsCollapsed={() => {}}
            onMobileClose={() => setMobileNavOpen(false)}
          />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header />
        <main className="bg-background flex-1 overflow-y-auto p-6 px-4 py-4">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
